import { css } from '@emotion/react'
import { parseISO, max } from 'date-fns'
import { Card, colors, font, mq } from 'plume-ui'
import { useState, useEffect } from 'react'
import { fetchCompanyChart } from '@/lib/api/company'
import {
  isIPO,
  isPrivate,
  isResearchDone,
  isResearchUnvalued,
} from '@/lib/company'
import { formatDate } from '@/lib/date'
import { printError } from '@/lib/log'
import { formatWithComma, formatAmount } from '@/lib/number'
import {
  ChartFundingRoundDesc,
  CompanyChartData,
  CompanyChartEventData,
  CompanyChartNewsData,
  CompanyData,
  InitialSeries,
} from '../../../../types/api'
import { A } from '../../../atoms/A'
import { Modal, ModalProps } from '../../../atoms/Modal'
import {
  IPOFundraiseChart,
  IPOShareChart,
  PrivateFundraiseChart,
} from '../chart/DonutChart'
import { HorizontalEventsChart } from '../chart/HorizontalEventsChart'
import { EmptyNumber } from './EmptyNumber'
import {
  colorOfInitialSeries,
  colorOfRound,
  FundingRoundAndEmployeeTransitionChart,
} from './FundingRoundAndEmployeeTransitionChart'
import { IcomoonIcon } from '@/components/atoms/IcomoonIcon'

export const EnterpriseBasicInfo: React.FC<{
  company: CompanyData
}> = (props) => {
  const [data, setData] = useState<CompanyChartData | null>()

  useEffect(() => {
    setData(null)

    fetchCompanyChart(props.company.shortname)
      .then((result) => setData(result))
      .catch((e) => printError(e))
  }, [props.company.shortname])

  if (!isValidChartData(props.company, data)) {
    return null
  }

  return (
    <>
      <BasicFinanceInfo company={props.company} data={data!} />
      <GrowthTransition company={props.company} data={data!} />
    </>
  )
}

export const isValidChartData = (
  company: CompanyData,
  data: CompanyChartData | null | undefined
): boolean => {
  if (!data) {
    return false
  }
  if (isPrivate(company)) {
    return !!data.privateFundraiseRatio
  } else {
    return !!(data.ipoFundraiseRatio && data.ipoShareRatio)
  }
}

const BasicFinanceInfo: React.FC<{
  company: CompanyData
  data: CompanyChartData
}> = (props) => {
  const formattedValuation = props.company.valuation
    ? formatAmount(props.company.valuation, {
        estimated: props.company.hasEstimatedRound,
      })
    : ''
  const formattedTotalFundraise = props.company.totalFundraise
    ? formatAmount(props.company.totalFundraise, {
        estimated: props.company.hasEstimatedRound,
      })
    : ''
  const formattedAmount = props.company.latestFundraise?.amount
    ? formatAmount(props.company.latestFundraise.amount, {
        estimated: props.company.hasEstimatedRound,
      })
    : ''
  return (
    <Card
      css={css`
        border-top: 1px solid ${colors.border};
        border-bottom: 1px solid ${colors.border};
      `}
    >
      <div
        css={css`
          display: flex;
          padding: 35px 25px;
          justify-content: space-between;
        `}
      >
        <div
          css={css`
            flex-basis: 35%;
          `}
        >
          {isIPO(props.company) ? (
            <FinanceInfoItem
              label="IPO時 時価総額"
              labelAnnotation="（初値）"
              value={formatWithComma(props.company.ipoData?.ipoMarketCap)}
              unit={'百万円'}
            />
          ) : (
            <FinanceInfoItem
              label="調達後評価額"
              labelAnnotation="（潜在株を含む）"
              value={formattedValuation ? formattedValuation.value : ''}
              unit={formattedValuation ? formattedValuation.unit : ''}
              valueAnnotationDesc="算出ラウンド"
              valueAnnotationDate={
                props.company.valuation &&
                props.company.financeBasicInfo?.lastValuationDate
                  ? formatDate(
                      props.company.financeBasicInfo.lastValuationDate,
                      'yyyy/MM/dd'
                    )
                  : null
              }
            />
          )}
          <FinanceInfoItem
            label={'総調達額'}
            value={formattedTotalFundraise ? formattedTotalFundraise.value : ''}
            unit={formattedTotalFundraise ? formattedTotalFundraise.unit : ''}
            valueAnnotationDesc="最新対象ラウンド"
            valueAnnotationDate={
              props.company.totalFundraise &&
              props.company.latestFundraise?.date
                ? formatDate(props.company.latestFundraise.date, 'yyyy/MM/dd')
                : null
            }
            css={css`
              margin-top: 20px;
            `}
          />
          <FinanceInfoItem
            label="最新ラウンド調達額"
            value={formattedAmount ? formattedAmount.value : ''}
            unit={formattedAmount ? formattedAmount.unit : ''}
            valueAnnotationDesc="対象ラウンド"
            valueAnnotationDate={
              props.company.latestFundraise?.date &&
              formatDate(props.company.latestFundraise.date, 'yyyy/MM/dd')
            }
            unknownValue={props.company.latestFundraise && <UnknownValue />}
            css={css`
              margin-top: 20px;
            `}
          />
        </div>
        {isPrivate(props.company) ? (
          <div
            css={css`
              margin-right: 200px;
            `}
            data-testid="fundraised-chart"
          >
            <PrivateFundraiseChart data={props.data.privateFundraiseRatio} />
          </div>
        ) : (
          <div
            css={css`
              display: flex;
            `}
            data-testid="fundraised-chart"
          >
            <IPOFundraiseChart
              data={props.data.ipoFundraiseRatio}
              css={css`
                margin-right: 50px;
              `}
            />
            <IPOShareChart data={props.data.ipoShareRatio} />
          </div>
        )}
      </div>
    </Card>
  )
}

const FinanceInfoItem: React.FC<{
  label: string
  labelAnnotation?: string
  value: string
  unit: string
  valueAnnotationDesc?: string
  valueAnnotationDate?: string | null
  unknownValue?: React.ReactNode
  className?: string
}> = (props) => {
  return (
    <div
      css={css`
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
      `}
      className={props.className}
    >
      <div
        css={css`
          flex: 1;
        `}
      >
        <div
          css={css`
            color: ${colors.textStrong};
            font-weight: ${font.weight.bold};
            font-size: 14px;
            margin-top: 5px;
          `}
        >
          {props.label}
        </div>
        <div
          css={css`
            color: ${colors.textLight};
            font-size: 13px;
            margin-top: 5px;
          `}
        >
          {props.labelAnnotation}
        </div>
      </div>
      <div
        css={css`
          flex: 1;
        `}
      >
        <div
          css={css`
            font-size: 20px;
          `}
        >
          {!props.value && props.unknownValue ? (
            props.unknownValue
          ) : (
            <>
              {props.value || <EmptyNumber />}
              <span
                css={css`
                  font-size: 13px;
                  color: ${colors.textLight};
                  margin-left: 5px;
                `}
              >
                {props.unit}
              </span>
            </>
          )}
        </div>
        {props.valueAnnotationDate ? (
          <div
            css={css`
              font-size: 13px;
              color: ${colors.textLight};
              margin-top: 5px;
            `}
          >
            {props.valueAnnotationDesc}：
            <span
              css={css`
                font-size: 14px;
                color: ${colors.textLight};
              `}
            >
              {props.valueAnnotationDate}
            </span>
          </div>
        ) : (
          <div
            css={css`
              width: 166px;
            `}
          />
        )}
      </div>
    </div>
  )
}

const UnknownValue = () => (
  <span
    css={css`
      color: ${colors.black30};
    `}
  >
    不明
  </span>
)

const GrowthTransition: React.FC<{
  company: CompanyData
  data: CompanyChartData
}> = (props) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const endDate = () => {
    if (isIPO(props.company) && props.company.ipoDate) {
      return parseISO(props.company.ipoDate)
    }
    if (isResearchDone(props.company) || isResearchUnvalued(props.company)) {
      const lastEventDate =
        props.data.events[props.data.events.length - 1]?.date
      const lastNewsDate = props.data.news[props.data.news.length - 1]?.date
      const lastRoundDate =
        props.data.fundingRounds[props.data.fundingRounds.length - 1]?.date

      const dates = [lastEventDate, lastNewsDate, lastRoundDate]
        .filter((d) => d !== undefined)
        .map((d) => parseISO(d))

      if (dates.length > 0) {
        return max(dates)
      }
    }
    return new Date()
  }

  return (
    <>
      <Card data-testid="growth-transition">
        <div
          css={css`
            padding: 36px 25px;
          `}
        >
          <div
            css={css`
              font-weight: ${font.weight.bold};
              font-size: 15px;
              line-height: 1.46;
              ${mq.extraLarge} {
                padding-left: 30px;
              }
            `}
          >
            成長の推移
            <span onClick={() => setIsModalOpen(true)}>
              <IcomoonIcon
                name="question-circle-fill"
                css={css`
                  font-size: 15px;
                  padding-left: 3px;
                  cursor: pointer;
                `}
              />
            </span>
          </div>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
            `}
          >
            <HorizontalEventsChart data={props.data} endDate={endDate()} />
            <FundingRoundAndEmployeeTransitionChart
              fundingRounds={props.data.fundingRounds}
              employeesShift={props.company.employeesShift || []}
              endDate={endDate()}
            />
          </div>
        </div>
      </Card>
      <GrowthTransitionModal
        isHidden={!isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  )
}

const GrowthTransitionModal: React.FC<ModalProps> = (props) => {
  return (
    <Modal
      {...props}
      css={css`
        max-width: 900px;
        .modal-content {
          padding: 55px 50px;
        }
      `}
    >
      <div>
        <ModalTitle
          css={css`
            margin-top: 0;
          `}
        >
          成長の推移
        </ModalTitle>
        <div>
          {FUNDING_ROUND_TYPE_LIST.map((types, i) => (
            <div
              key={i}
              css={css`
                display: flex;
                margin-top: 8px;
              `}
            >
              <span
                css={css`
                  background-color: ${colorOfRound(types[0])};
                  width: 18px;
                  height: 18px;
                  border-radius: 50%;
                  margin-right: 10px;
                  min-width: 18px;
                `}
              />
              <div
                css={css`
                  font-size: 14px;
                  line-height: 1.5;
                `}
              >
                {types.join('、')}
              </div>
            </div>
          ))}
        </div>
        <ModalTitle>スピーダ調達シリーズ</ModalTitle>
        <div>
          スピーダの定義に基づいたシリーズ。定義については
          <A
            href={process.env.INITIAL_SERIES_HELP_ENDPOINT}
            target="_blank"
            decoration="none"
          >
            こちら
          </A>
          <div
            css={css`
              display: flex;
              margin-top: 12px;
            `}
          >
            {SERIES_LIST.map((s, i) => (
              <div
                key={i}
                css={css`
                  height: 42px;
                  width: 115px;
                  border: 1px solid ${colors.black10};
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  background-color: ${colorOfInitialSeries(s)};
                  font-size: 14px;
                  line-height: 1.5;
                  &:not(:first-of-type) {
                    border-left: 0;
                  }
                `}
              >
                {s}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div>
        <ModalTitle>イベント</ModalTitle>
        <div
          css={css`
            font-size: 14px;
            line-height: 1.5;
          `}
        >
          {EVENT_TYPE_LIST.join('、')}
        </div>
      </div>
      <div>
        <ModalTitle>ニュース</ModalTitle>
        <div
          css={css`
            font-size: 14px;
            line-height: 1.5;
          `}
        >
          {NEWS_TYPE_LIST.join('、')}
        </div>
      </div>
    </Modal>
  )
}

type ModalTitleProps = {
  className?: string
  children: React.ReactNode
}

const ModalTitle: React.FC<ModalTitleProps> = (props) => {
  return (
    <div
      css={css`
        font-size: 14px;
        line-height: 1.5;
        font-weight: ${font.weight.bold};
        margin-top: 25px;
        margin-bottom: 5px;
      `}
      className={props.className}
    >
      {props.children}
    </div>
  )
}

const FUNDING_ROUND_TYPE_LIST: ChartFundingRoundDesc[][] = [
  ['有償第三者割当', '有償株主割当'],
  [
    '公募増資',
    '増資',
    '資本組み入れ',
    '現金出資・現物出資',
    'クラウドファンディング',
  ],
  ['調達方法不明', '短期借入(Loan)', '長期借入(Debt)'],
  ['創業', '会社設立', '合併（M&A,事業継承など）'],
  [
    '新株予約権の付与（発行）',
    '新株引受権付社債の発行',
    '新株予約権付社債の発行',
    '転換社債型新株予約権付社債の発行',
    '転換社債の発行',
    '転換ローン(Convertible Note)の発行',
    '転換ローン(Convertible Note)の発行 (日本版)',
    '新株引受権の発行',
  ],
  [
    '新株予約権の権利行使',
    '新株引受権付社債の権利行使',
    '新株予約権付社債の権利行使',
    'グリーンシート',
    '転換社債型新株予約権付社債の権利行使',
    '転換社債の株式転換',
    '転換ローン(Convertible Note)の株式転換',
    '転換ローン(Convertible Note)の株式転換 (日本版)',
    '新株引受権の権利行使',
  ],
  ['株式の分割', '株式の移動'],
  ['従業員数'],
  ['不明'],
]

const SERIES_LIST: InitialSeries[] = [
  'シード',
  'シリーズA',
  'シリーズB',
  'シリーズC',
  'シリーズD',
  'シリーズE',
  'シリーズF',
  'シリーズG',
]

const EVENT_TYPE_LIST: CompanyChartEventData['type'][] = [
  '創業',
  '会社設立',
  '合併（M&A,事業継承など）',
  '被買収・子会社化・主要株式取得',
  '事業提携',
  '事業譲渡',
  '事業譲受',
  '破綻・清算・解散',
  '住所変更',
  'IPO',
]

const NEWS_TYPE_LIST: CompanyChartNewsData['type'][] = [
  'ファイナンス・M&A',
  '外部ニュース',
]
